import type { TeamWith } from "@redotech/account-common/base-team";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { LoadState, useLoad } from "@redotech/react-util/load";
import { ScreenType, useScreenType } from "@redotech/react-util/screen";
import { FulfillmentOrderLineItem } from "@redotech/redo-fulfillment-group-common/fulfillment-order-line-item";
import type { OutboundLabelSettings } from "@redotech/redo-fulfillment-settings/outbound-label-settings";
import { PickSessionItem } from "@redotech/redo-fulfillment-wms-common/schema/pick-session";
import { useTranslation } from "@redotech/redo-i18n";
import XIcon from "@redotech/redo-icons/x-close.svg";
import { LocationId } from "@redotech/redo-inventory-id";
import { ProductId } from "@redotech/redo-product-id";
import { StorageLocationId } from "@redotech/redo-warehouse-id";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSpinner } from "@redotech/redo-web/arbiter-components/loading/redo-spinner";
import { RedoBaseModal } from "@redotech/redo-web/arbiter-components/modal/redo-base-modal";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Divider } from "@redotech/redo-web/divider";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import classNames from "classnames";
import { memo, useEffect, useMemo, useState } from "react";
import { RedoOutboundLabelsRpcClientContext } from "../../outbound-labels-rpc-client";
import { LotBadge } from "../shared/lot-badge";
import { QuantityIncrementDecrement } from "../shared/quantity-increment-decrement";
import * as styles from "./tote-scanning-page.module.css";
import {
  getPickTitle,
  isItemMatch,
  playErrorSound,
  playSuccessSound,
} from "./util";

interface BinOption {
  storageLocationId: StorageLocationId;
  binName: string;
  availableQuantity: number;
  lastCountedAt?: Date | null;
  isReceive?: boolean;
  lotNumber?: string | null;
  expirationDate?: Date | null;
}

const RECEIVE_SENTINEL_ID = "sl_RECEIVE" as StorageLocationId;

export interface BinQuantitySource {
  storageLocationId: StorageLocationId | null;
  quantity: number;
}

interface BinQuantityModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (
    quantity: number,
    sourceStorageLocationId?: StorageLocationId,
    receiveFromExternal?: boolean,
    sources?: BinQuantitySource[],
  ) => void;
  onConfirmLoad: LoadState<void>;
  item: PickSessionItem | FulfillmentOrderLineItem;
  quantityLeftToPick: number;
  team?: TeamWith<{ outboundLabels: OutboundLabelSettings }> | null;
  scanValue: string | null;
  resetScanValue: () => void;
  requireSourceBin?: boolean;
  locationId?: LocationId;
  productId?: ProductId;
}

export const BinQuantityModal = memo(function BinQuantityModal({
  isOpen,
  onClose,
  onConfirm,
  item,
  quantityLeftToPick,
  team,
  scanValue,
  onConfirmLoad,
  resetScanValue,
  requireSourceBin,
  locationId,
  productId,
}: BinQuantityModalProps) {
  const { t } = useTranslation();
  const rpcClient = useRequiredContext(RedoOutboundLabelsRpcClientContext);
  const [selectedQuantity, setSelectedQuantity] = useState(
    Math.min(1, quantityLeftToPick),
  );
  const screenType = useScreenType();
  const isMobile = screenType === ScreenType.MOBILE;

  const [selectedBin] = useState<RedoListItem<BinOption> | null>(null);
  const [allocations, setAllocations] = useState<Map<string, number>>(
    new Map(),
  );
  const [visibleBinIds, setVisibleBinIds] = useState<Set<string>>(new Set());
  const [userEditedAllocations, setUserEditedAllocations] = useState(false);

  const binsLoad = useLoad(
    async (signal) => {
      if (!requireSourceBin || !locationId || !productId) {
        return undefined;
      }
      const result = await rpcClient.getBinsForProduct(
        { locationId, productId, includeStorage: true, includeUncounted: true },
        { signal },
      );
      return {
        bins: result.bins,
        defaultStorageLocationId: result.defaultStorageLocationId,
      };
    },
    [rpcClient, requireSourceBin, locationId, productId],
  );

  useEffect(() => {
    if (!isOpen) {
      setAllocations(new Map());
      setVisibleBinIds(new Set());
      setUserEditedAllocations(false);
    }
  }, [isOpen]);

  const availableBinOptions: BinOption[] = useMemo(() => {
    return binsLoad.value?.bins ?? [];
  }, [binsLoad.value?.bins]);

  useEffect(() => {
    if (!requireSourceBin) {
      return;
    }
    if (userEditedAllocations) {
      return;
    }
    const initial = new Map<string, number>();
    const visible = new Set<string>();
    let remaining = quantityLeftToPick;
    for (const bin of availableBinOptions) {
      if (remaining <= 0) {
        break;
      }
      const capacity =
        bin.lastCountedAt == null
          ? remaining
          : Math.min(remaining, bin.availableQuantity);
      if (capacity > 0) {
        initial.set(bin.storageLocationId, capacity);
        visible.add(bin.storageLocationId);
        remaining -= capacity;
      }
    }
    if (visible.size === 0 && availableBinOptions[0]) {
      visible.add(availableBinOptions[0].storageLocationId);
    }
    visible.add(RECEIVE_SENTINEL_ID);
    if (remaining > 0) {
      initial.set(RECEIVE_SENTINEL_ID, remaining);
    }
    setAllocations(initial);
    setVisibleBinIds(visible);
  }, [
    requireSourceBin,
    availableBinOptions,
    quantityLeftToPick,
    userEditedAllocations,
  ]);

  const totalAllocated = useMemo(
    () => [...allocations.values()].reduce((s, n) => s + n, 0),
    [allocations],
  );

  const updateAllocation = useHandler((binId: string, qty: number) => {
    setUserEditedAllocations(true);
    setAllocations((prev) => {
      const next = new Map(prev);
      if (qty <= 0) {
        next.delete(binId);
      } else {
        next.set(binId, qty);
      }
      return next;
    });
  });

  const showBin = useHandler((binId: string) => {
    setUserEditedAllocations(true);
    setVisibleBinIds((prev) => {
      const next = new Set(prev);
      next.add(binId);
      return next;
    });
  });

  const hideBin = useHandler((binId: string) => {
    setUserEditedAllocations(true);
    setVisibleBinIds((prev) => {
      const next = new Set(prev);
      next.delete(binId);
      return next;
    });
    setAllocations((prev) => {
      const next = new Map(prev);
      next.delete(binId);
      return next;
    });
  });

  const buildSources = (): BinQuantitySource[] => {
    return [...allocations.entries()]
      .filter(([, qty]) => qty > 0)
      .map(([binId, quantity]) => ({
        storageLocationId:
          binId === RECEIVE_SENTINEL_ID ? null : (binId as StorageLocationId),
        quantity,
      }));
  };

  const selectedBinIsReceive = selectedBin?.value.isReceive === true;

  const selectedBinIsUncounted =
    selectedBin && !selectedBin.value.lastCountedAt && !selectedBinIsReceive;

  const maxQuantity =
    requireSourceBin && selectedBin && !selectedBinIsUncounted
      ? selectedBin.value.isReceive
        ? quantityLeftToPick
        : Math.min(quantityLeftToPick, selectedBin.value.availableQuantity)
      : quantityLeftToPick;

  const handleBarcodeScanned = useHandler(async (scannedValue: string) => {
    if (!isItemMatch(item, scannedValue, team)) {
      toast(`Item does not match scanned value: ${scannedValue}`, {
        variant: "error",
      });
      void playErrorSound();
      resetScanValue();
      return;
    }

    if (requireSourceBin) {
      if (totalAllocated >= quantityLeftToPick) {
        toast(`Cannot pick more than ${quantityLeftToPick} items`, {
          variant: "error",
        });
        void playErrorSound();
        resetScanValue();
        return;
      }
      const nextBin = availableBinOptions.find((bin) => {
        const current = allocations.get(bin.storageLocationId) ?? 0;
        const cap =
          bin.lastCountedAt == null
            ? quantityLeftToPick
            : bin.availableQuantity;
        return current < cap;
      });
      if (!nextBin) {
        updateAllocation(
          RECEIVE_SENTINEL_ID,
          (allocations.get(RECEIVE_SENTINEL_ID) ?? 0) + 1,
        );
      } else {
        updateAllocation(
          nextBin.storageLocationId,
          (allocations.get(nextBin.storageLocationId) ?? 0) + 1,
        );
      }
      void playSuccessSound();
      resetScanValue();
      return;
    }

    if (selectedQuantity >= maxQuantity) {
      toast(`Cannot pick more than ${maxQuantity} items`, { variant: "error" });
      void playErrorSound();
    } else {
      const newQuantity = Math.min(selectedQuantity + 1, maxQuantity);
      if (newQuantity === maxQuantity) {
        void playSuccessSound();
        const isReceive = selectedBin?.value.isReceive || false;
        const sourceId = isReceive
          ? undefined
          : selectedBinIsUncounted
            ? (binsLoad.value?.defaultStorageLocationId ?? undefined)
            : selectedBin?.value.storageLocationId;
        onConfirm(newQuantity, sourceId, isReceive);
      } else {
        setSelectedQuantity(newQuantity);
        void playSuccessSound();
      }
    }
    resetScanValue();
  });

  useEffect(() => {
    if (scanValue) {
      void handleBarcodeScanned(scanValue);
    }
  }, [scanValue, handleBarcodeScanned]);

  const confirmDisabled = requireSourceBin
    ? totalAllocated <= 0 || totalAllocated > quantityLeftToPick
    : selectedQuantity <= 0;

  if (isOpen && requireSourceBin && (binsLoad.pending || !binsLoad.value)) {
    return (
      <RedoBaseModal
        isOpen={isOpen}
        onModalCloseRequested={onClose}
        otherClasses={[styles.modalContentCompact]}
      >
        <Flex
          align="center"
          dir="column"
          gap="md"
          justify="center"
          p="3xl"
          w="full"
        >
          <RedoSpinner />
          <Text fontSize="sm" textColor="secondary">
            {t("Loading bins...")}
          </Text>
        </Flex>
      </RedoBaseModal>
    );
  }

  return (
    <RedoBaseModal
      isOpen={isOpen}
      onModalCloseRequested={onClose}
      otherClasses={[styles.modalContentCompact]}
    >
      <Flex dir="column" gap="none" p={isMobile ? "xl" : "3xl"} w="full">
        <Flex align="center" justify="space-between" w="full">
          <Flex dir="column" gap="xs">
            <Text fontSize={isMobile ? "md" : "lg"} fontWeight="semibold">
              {t("Confirm quantity ({{selected}}/{{total}})", {
                selected: requireSourceBin ? totalAllocated : selectedQuantity,
                total: quantityLeftToPick,
              })}
            </Text>
            {!isMobile && (
              <Text fontSize="sm" textColor="secondary">
                {t("Scan each item to pick, or adjust quantity manually.")}
              </Text>
            )}
          </Flex>
          <RedoButton
            hierarchy="tertiary"
            IconLeading={XIcon}
            onClick={onClose}
            size={isMobile ? "md" : "xl"}
          />
        </Flex>

        <Flex align="center" gap="md" py={isMobile ? "sm" : "3xl"} w="full">
          {item.imageUrl ? (
            <img
              alt={`Product ${item.sku}`}
              className={classNames(
                styles.productImage,
                isMobile && styles.productImageCompact,
              )}
              src={item.imageUrl}
            />
          ) : (
            <div
              className={classNames(
                styles.productImagePlaceholder,
                isMobile && styles.productImagePlaceholderCompact,
              )}
            />
          )}
          <Flex dir="column" gap="xxs" grow="1">
            {"bin" in item && item.bin && (
              <Text fontSize={isMobile ? "md" : "lg"} fontWeight="bold">
                {t("Bin:")} {item.bin}
              </Text>
            )}
            <Text fontSize={isMobile ? "xs" : "sm"} textColor="secondary">
              {getPickTitle(item)}
            </Text>
            {item.sku && (
              <Text fontSize="xs" textColor="tertiary">
                {t("SKU:")} {item.sku}
              </Text>
            )}
            {item.customAttributes.find((ca) =>
              ca.key.toLowerCase().includes("brand style"),
            ) && (
              <Text fontSize="xs" textColor="tertiary">
                {t("Brand style:")}{" "}
                {
                  item.customAttributes.find((ca) =>
                    ca.key.toLowerCase().includes("brand style"),
                  )?.value
                }
              </Text>
            )}
          </Flex>
        </Flex>

        <Divider classes={styles.lightDivider} />

        {requireSourceBin ? (
          <Flex dir="column" gap="md" py="md" w="full">
            <Flex align="center" dir="row" justify="space-between">
              <Text fontSize="sm" fontWeight="semibold">
                {t("Source bins")}
              </Text>
              <Text
                fontSize="xs"
                textColor={
                  totalAllocated === quantityLeftToPick ? "primary" : "warning"
                }
              >
                {totalAllocated} / {quantityLeftToPick}
              </Text>
            </Flex>
            {binsLoad.pending ? (
              <Flex align="center" justify="center" p="xl">
                <RedoSpinner />
              </Flex>
            ) : availableBinOptions.length === 0 ? (
              <Text fontSize="xs" textColor="tertiary">
                {t("No bins with available inventory")}
              </Text>
            ) : (
              <>
                {availableBinOptions
                  .filter((bin) => visibleBinIds.has(bin.storageLocationId))
                  .map((bin) => {
                    const qty = allocations.get(bin.storageLocationId) ?? 0;
                    const isUncounted = bin.lastCountedAt == null;
                    const cap = isUncounted
                      ? quantityLeftToPick
                      : Math.min(quantityLeftToPick, bin.availableQuantity);
                    return (
                      <Flex
                        align="center"
                        dir="row"
                        gap="md"
                        key={bin.storageLocationId}
                        w="full"
                      >
                        <Flex dir="column" grow={1}>
                          <Flex align="center" dir="row" gap="xs">
                            <Text fontSize="sm">{bin.binName}</Text>
                            {bin.lotNumber && (
                              <LotBadge
                                expirationDate={bin.expirationDate}
                                lotNumber={bin.lotNumber}
                              />
                            )}
                          </Flex>
                          <Text fontSize="xxs" textColor="quaternary">
                            {isUncounted
                              ? t("{{count}} available (uncounted)", {
                                  count: bin.availableQuantity,
                                })
                              : t("{{count}} available", {
                                  count: bin.availableQuantity,
                                })}
                          </Text>
                        </Flex>
                        <Flex style={{ flexShrink: 0 }}>
                          <QuantityIncrementDecrement
                            compact
                            max={cap}
                            min={0}
                            setValue={(v) =>
                              updateAllocation(bin.storageLocationId, v)
                            }
                            size="sm"
                            value={qty}
                          />
                        </Flex>
                        <RedoButton
                          hierarchy="tertiary"
                          IconLeading={XIcon}
                          onClick={() => hideBin(bin.storageLocationId)}
                          size="xs"
                        />
                      </Flex>
                    );
                  })}
                {visibleBinIds.has(RECEIVE_SENTINEL_ID) && (
                  <Flex
                    align="center"
                    dir="row"
                    gap="md"
                    key={RECEIVE_SENTINEL_ID}
                    w="full"
                  >
                    <Flex dir="column" grow={1}>
                      <Text fontSize="sm">{t("Receive (from external)")}</Text>
                      <Text fontSize="xxs" textColor="quaternary">
                        {t("Bring in from outside inventory")}
                      </Text>
                    </Flex>
                    <Flex style={{ flexShrink: 0 }}>
                      <QuantityIncrementDecrement
                        compact
                        max={quantityLeftToPick}
                        min={0}
                        setValue={(v) =>
                          updateAllocation(RECEIVE_SENTINEL_ID, v)
                        }
                        size="sm"
                        value={allocations.get(RECEIVE_SENTINEL_ID) ?? 0}
                      />
                    </Flex>
                    <RedoButton
                      hierarchy="tertiary"
                      IconLeading={XIcon}
                      onClick={() => hideBin(RECEIVE_SENTINEL_ID)}
                      size="xs"
                    />
                  </Flex>
                )}
                {(() => {
                  const hiddenBins = availableBinOptions.filter(
                    (bin) => !visibleBinIds.has(bin.storageLocationId),
                  );
                  const receiveHidden = !visibleBinIds.has(RECEIVE_SENTINEL_ID);
                  if (hiddenBins.length === 0 && !receiveHidden) {
                    return null;
                  }
                  const options: RedoListItem<string>[] = [
                    ...hiddenBins.map((bin) => {
                      const availText =
                        bin.lastCountedAt == null
                          ? `${bin.availableQuantity} available (uncounted)`
                          : `${bin.availableQuantity} available`;
                      const lotText = bin.lotNumber
                        ? ` · ${bin.lotNumber}${
                            bin.expirationDate
                              ? ` · Exp ${bin.expirationDate.toISOString().slice(0, 10)}`
                              : ""
                          }`
                        : "";
                      return {
                        id: bin.storageLocationId,
                        value: bin.storageLocationId,
                        type: "text" as const,
                        text: `${bin.binName}${lotText}`,
                        supportingText: availText,
                      };
                    }),
                    ...(receiveHidden
                      ? [
                          {
                            id: RECEIVE_SENTINEL_ID,
                            value: RECEIVE_SENTINEL_ID,
                            type: "text" as const,
                            text: t("Receive (from external)"),
                          },
                        ]
                      : []),
                  ];
                  return (
                    <RedoSingleSelectDropdownInput
                      options={options}
                      optionSelected={({ item: selected }) => {
                        showBin(selected.value);
                      }}
                      placeholder={t("Add another bin…")}
                      size="sm"
                    />
                  );
                })()}
              </>
            )}
          </Flex>
        ) : (
          <QuantityIncrementDecrement
            compact={isMobile}
            max={maxQuantity}
            min={0}
            setValue={setSelectedQuantity}
            subtitle={t("Remaining: {{count}}", {
              count: quantityLeftToPick - selectedQuantity,
            })}
            title={t("Quantity")}
            value={selectedQuantity}
          />
        )}

        <Flex dir="column" gap={isMobile ? "sm" : "md"} w="full">
          <RedoButton
            className="w-full"
            disabled={confirmDisabled}
            hierarchy="primary"
            onClick={() => {
              if (requireSourceBin) {
                onConfirm(totalAllocated, undefined, false, buildSources());
                return;
              }
              const isReceive = selectedBin?.value.isReceive || false;
              const sourceId = isReceive
                ? undefined
                : selectedBinIsUncounted
                  ? (binsLoad.value?.defaultStorageLocationId ?? undefined)
                  : selectedBin?.value.storageLocationId;
              onConfirm(selectedQuantity, sourceId, isReceive);
            }}
            pending={onConfirmLoad.pending}
            size={isMobile ? "md" : "xl"}
            style={
              !confirmDisabled
                ? { backgroundColor: "#f54a00", outlineColor: "#f54a00" }
                : undefined
            }
            text={t("Confirm")}
            theme="normal"
          />
          <RedoButton
            className="w-full"
            hierarchy="secondary"
            onClick={onClose}
            size={isMobile ? "md" : "xl"}
            text={t("Cancel")}
          />
        </Flex>
      </Flex>
    </RedoBaseModal>
  );
});
